// shared/components/customer-select/customer-select.component.ts
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Customer } from '@shared/models/customer.model';
import { CustomerService } from '@shared/services/customer.service';
import { AuthService } from '@auth/auth.service';

@Component({
  selector: 'app-customer-select',
  templateUrl: './customer-select.component.html',
  styleUrls: ['./customer-select.component.scss']
})
export class CustomerSelectComponent implements OnInit, OnDestroy {
  @Input() customerId: string | null = null;
  @Input() customerName: string | null = null;
  @Input() limitToCurrentUser = false;

  @Output() selected = new EventEmitter<Customer>();

  results: Customer[] = [];
  noAssignableCustomers = false;

  private destroy$ = new Subject<void>();
  private currentUsername: string | null = null;

  constructor(
    private customerSvc: CustomerService,
    private authService: AuthService,
  ) { }

  ngOnInit(): void {
    const currentUser = this.authService.getToken()?.user || null;
    if (currentUser) {
      this.currentUsername = currentUser.username || currentUser.email || null;
    }

    this.loadCustomers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCustomers(): void {
    // Dropdown mode: always fetch all (limit 1000)
    const query: any = { enabled: true, limit: 1000 };

    this.customerSvc.list(query)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          let items: any[] = res.items || [];

          // If restricted to current user sites/assignee
          if (this.limitToCurrentUser && this.currentUsername) {
            items = items.filter(c =>
              Array.isArray(c.assignedUsers) &&
              c.assignedUsers.includes(this.currentUsername)
            );
            this.noAssignableCustomers = items.length === 0;
          }

          this.results = items;

          // Note: We don't need to manually sync customerId here as [value] bindings handle display,
          // but we could validate if the selected ID exists in the loaded list.
        },
        error: err => {
          console.error('Failed to load customers', err);
          this.results = [];
        }
      });
  }

  onSelectChange(event: any): void {
    const selectedId = event.target.value;
    const found = this.results.find(c => c._id === selectedId);

    if (found) {
      this.customerId = found._id;
      this.customerName = found.name;
      this.selected.emit(found);
    } else {
      this.customerId = null;
      this.customerName = null;
      this.selected.emit(null!); // Emit null to indicate clear
    }
  }
}
